/**
 * Normalizes a phone number to E.164 format.
 *
 * E.164 format: +[country code][area code][local number]
 * Example: +14155551234
 *
 * This function ONLY supports US/Canada phone numbers (country code +1).
 * International numbers with other country codes will be rejected.
 *
 * @param phoneNumber - The phone number to normalize (can be in various formats)
 * @returns The phone number in E.164 format, or null if invalid
 */
export function normalizePhoneNumber(phoneNumber: string | null | undefined): string | null {
  if (!phoneNumber) {
    return null
  }

  // Remove all non-digit characters except leading +
  let cleaned = phoneNumber.replace(/[^\d+]/g, '')

  // SECURITY: Explicitly reject international numbers with country codes other than +1
  // We only support US/Canada (+1) at this time
  if (cleaned.startsWith('+') && !cleaned.startsWith('+1')) {
    // Number has a country code that is not +1
    return null
  }

  // If it starts with +1, strip the country code
  if (cleaned.startsWith('+1')) {
    cleaned = cleaned.substring(2)
  }
  // If it starts with 1 (11 digits total), remove the leading 1
  else if (cleaned.startsWith('1') && cleaned.length === 11) {
    cleaned = cleaned.substring(1)
  }

  // At this point, cleaned should be a 10-digit US number
  if (cleaned.length !== 10) {
    return null
  }

  // Validate it's all digits
  if (!/^\d{10}$/.test(cleaned)) {
    return null
  }

  // Return in E.164 format (US country code +1)
  return `+1${cleaned}`
}
